import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';

export type FavouriteType = 'track' | 'album' | 'artist' | 'playlist';

// Tracks and toggles favourite status for a single (type, id) pair - the
// heart button in the album/artist/playlist detail hero. Fetches the
// current status on mount/id change, then updates optimistically on toggle
// (reverting if the request fails) so the heart responds instantly.
export function useFavourite(type: FavouriteType, id: number | undefined) {
  const [favourited, setFavourited] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setFavourited(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/favourite?type=${type}&id=${id}`, {
          credentials: 'include',
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) setFavourited(Boolean(data.favourited));
      } catch {
        // Leave favourited at its previous value - a failed status check
        // isn't worth surfacing as an error for a single heart icon.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type, id]);

  const toggle = useCallback(async () => {
    if (!id || loading) return;
    const next = !favourited;
    setFavourited(next);
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/favourite${next ? '' : `?type=${type}&id=${id}`}`, {
        method: next ? 'POST' : 'DELETE',
        credentials: 'include',
        headers: next ? { 'Content-Type': 'application/json' } : undefined,
        body: next ? JSON.stringify({ type, id }) : undefined,
      });
      if (!response.ok) setFavourited(!next);
    } catch {
      setFavourited(!next);
    } finally {
      setLoading(false);
    }
  }, [type, id, favourited, loading]);

  return { favourited, toggle };
}
